import React, { useEffect, useRef } from 'react';

// Same focus containment approach as the existing onboarding wizard.
export function ProgrammingDialog({ children, label, onClose, saving }: {
  children: React.ReactNode; label: string; onClose: () => void; saving: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef(document.activeElement as HTMLElement | null);
  const callbacks = useRef({ onClose, saving });
  callbacks.current = { onClose, saving };
  useEffect(() => {
    const dialog = ref.current!;
    const previous = opener.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'
    )).filter(el => !el.hidden && el.getAttribute('aria-hidden') !== 'true');
    const focusFirst = () => (focusable()[0] || dialog).focus();
    focusFirst();
    const keydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!callbacks.current.saving) callbacks.current.onClose();
      }
      if (e.key !== 'Tab') return;
      const nodes = focusable(), first = nodes[0], last = nodes[nodes.length - 1];
      if (!first) { e.preventDefault(); dialog.focus(); return; }
      if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
        e.preventDefault(); first.focus();
      }
    };
    const focusin = (e: FocusEvent) => { if (!dialog.contains(e.target as Node)) focusFirst(); };
    document.addEventListener('keydown', keydown);
    document.addEventListener('focusin', focusin);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('focusin', focusin);
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return <div ref={ref} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>{children}</div>;
}
